import { ref } from 'vue';
import { socket } from '@/socket';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useAlerts from '@/composables/useAlerts';
const { decryptSymbols } = useDecryptSymbols();
const { addAlert } = useAlerts();

// Create state outside the composable to persist across component instances
const state = {
  duplicates: ref([]),
  progress: ref(0),
  isSearching: ref(false),
  searchingType: ref('companies'),
};

// Create socket event handlers outside the composable
const handleDuplicateFound = (duplicate) => {
  const decryptedDuplicate = decryptSymbols(duplicate);
  if (decryptedDuplicate.type == state.searchingType.value) {
    state.duplicates.value.push(decryptedDuplicate);
  }
};

const handleProgress = (percentage) => {
  state.progress.value = percentage;
};

const handleComplete = () => {
  state.isSearching.value = false;
  addAlert('Duplicate search complete!', 'low', 5);
  console.log('duplicateCheckComplete');
};

const handleStopped = () => {
  state.isSearching.value = false;
  addAlert('Duplicate search stopped before completion', 'low', 5);
  console.log('duplicateCheckStopped');
};

const handleAlreadyInProgress = () => {
  state.isSearching.value = false;
  addAlert('A duplicate search is already in progress', 'low', 5);
  console.log('duplicateCheckAlreadyInProgress');
};

const handleDevNotice = () => {
  addAlert('Duplicate search is in development. Check back soon!', 'low', 5);
};

export default function useAIFindDuplicates() {
  // Setup socket event listeners if they haven't been set up yet
  if (!socket.hasListeners('duplicateFound')) {
    socket.on('duplicateFound', handleDuplicateFound);
    socket.on('duplicateProgress', handleProgress);
    socket.on('duplicateCheckComplete', handleComplete);
    socket.on('duplicateCheckStopped', handleStopped);
    socket.on('duplicateCheckAlreadyInProgress', handleAlreadyInProgress);
    socket.on('duplicateCheckDevNotice', () => {
      state.isSearching.value = false;
      handleDevNotice();
    });
  }

  socket.on('disconnect', () => {
    if (state.isSearching.value) {
      addAlert('Duplicate search interrupted!', 'low', 5);
    }
    stopDuplicateCheck();
  });

  const startDuplicateCheck = (type) => {
    state.isSearching.value = true;
    state.duplicates.value = [];
    state.progress.value = 0;
    state.searchingType.value = type;
    socket.emit('startDuplicateCheck', type);
  };

  const stopDuplicateCheck = () => {
    state.isSearching.value = false;
    socket.emit('stopDuplicateCheck');
  };

  const clearResults = () => {
    state.duplicates.value = [];
    state.progress.value = 0;
    state.isSearching.value = false;
  };

  const setNotDuplicate = (id, duplicateId, type) => {
    socket.emit('setNotDuplicate', { id, duplicateId, type });
  };

  // Return the shared state and methods
  return {
    duplicates: state.duplicates,
    progress: state.progress,
    isSearching: state.isSearching,
    searchingType: state.searchingType,
    startDuplicateCheck,
    stopDuplicateCheck,
    clearResults,
    setNotDuplicate,
  };
}
